import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, X, RotateCcw, Check, ChevronDown, ChevronRight, GripVertical, Camera, Pencil, MessageSquare, Paperclip, Link2, Download, FileText, ExternalLink, CalendarPlus, FolderPlus } from 'lucide-react';
import { getAccessToken } from '../services/auth';
import WishList from '../components/WishList';

// The Team board. One pool of projects; whoever has capacity claims one; the task list is built
// afterwards, in a sit-down with the person who took it.
//
// ⚠️ A PROJECT IN THE POOL SHOWS NO TASK LIST, ON PURPOSE. Greg, 2026-09-21: "none of these
// should have subtasks until the person claims it... I'll need to have a starter conversation
// with them." The server enforces it too - task_add refuses an unclaimed project - so a future
// edit to this file cannot quietly reintroduce guessed task lists.
//
// ⚠️ THE ROSTER STRIP SHOWS EVERY PERSON, ZEROS INCLUDED. An empty chip is the thing Greg scans
// for at 9:30 on a Tuesday, so somebody carrying nothing is drawn MORE loudly, not filtered out.
//
// ⚠️ This page never derives a number. Percentages, counts and the roster all come from
// team-board.js, so the board and anything else reading team_projects cannot drift apart.

const base = () => (window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions');

// Called directly rather than through neonQuery: that helper returns data.rows whenever the key
// exists and DISCARDS the rest of the envelope, which shipped a blank page here once already.
async function call(action, params = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base() + '/team-board', {
    method: 'POST', headers, body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.error || 'team-board ' + res.status); e.status = res.status; throw e; }
  return data;
}

// The R2 upload function, for project photos: a signed link to put the file up, and a signed
// link to read it back. Nothing here is a public URL.
async function r2(payload) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base() + '/r2-upload', { method: 'POST', headers, body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'upload ' + res.status);
  return data;
}

// ⚠️ iPHONE PHOTOS ARE HEIC, AND NO BROWSER HERE CAN SHOW ONE. Greg, 2026-10-06: "make it so
// HEICs work." So a HEIC/HEIF is turned into a JPG in the browser before it goes up - what is
// stored is always something every browser can display. The converter is 1.3 MB, so it is a
// file in public/vendor that loads only the first time somebody actually picks a HEIC.
let heicLoader = null;
function loadHeic() {
  if (window.heic2any) return Promise.resolve(window.heic2any);
  if (!heicLoader) {
    heicLoader = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = (process.env.PUBLIC_URL || '') + '/vendor/heic2any.min.js';
      el.onload = () => (window.heic2any ? resolve(window.heic2any) : reject(new Error('converter did not load')));
      el.onerror = () => { heicLoader = null; reject(new Error('converter did not load')); };
      document.head.appendChild(el);
    });
  }
  return heicLoader;
}
const isHeic = (f) => /hei[cf]/i.test(f.type || '') || /\.hei[cf]$/i.test(f.name || '');
async function browserReady(file) {
  if (!isHeic(file)) return file;
  const convert = await loadHeic();
  const out = await convert({ blob: file, toType: 'image/jpeg', quality: 0.85 });
  const blob = Array.isArray(out) ? out[0] : out;
  return new File([blob], String(file.name || 'photo').replace(/\.hei[cf]$/i, '') + '.jpg', { type: 'image/jpeg' });
}

// Put each picked file into storage and hand back what the board needs to record it. A file
// that will not go up is counted, never thrown - one bad photo must not lose the others.
async function uploadPhotos(files, each) {
  let failed = 0;
  for (const picked of files) {
    try {
      const file = await browserReady(picked);
      const sign = await r2({ action: 'presign-doc', filename: file.name, contentType: file.type, prefix: 'team-board' });
      const put = await fetch(sign.uploadUrl, { method: 'PUT', headers: { 'Content-Type': sign.contentType }, body: file });
      if (!put.ok) throw new Error('upload ' + put.status);
      await each({ key: sign.key, filename: file.name, contentType: sign.contentType });
    } catch (_) { failed++; }
  }
  return failed;
}
const failText = (n) => (n === 1 ? 'One file did not upload. Try it again.' : n + ' files did not upload. Try them again.');

const DOMAIN_LABEL = {
  fontFamily: 'monospace', fontSize: 10.5, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: 'var(--muted, #59707E)',
};

function Avatar({ name, size = 38, faded = false }) {
  const initials = String(name || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  return (
    <span style={{
      flexShrink: 0, width: size, height: size, borderRadius: 999,
      background: faded ? 'rgba(255,255,255,0.10)' : 'var(--navy)',
      color: faded ? '#8FC4E4' : '#fff', display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: size * 0.34, fontWeight: 700,
    }}>{initials}</span>
  );
}

// The steps on a project.
//
// ⚠️ TWO LEVELS OF "MAY TOUCH THIS". Jessica, 2026-10-06: "the ability to edit a step once it's
// been entered and the ability to tag/assign another team member to a step... The team member
// should be able to add notes/comments and upload media to the Step/task and it updates for
// everyone."
//   full  = the project's owner or a steward: reword, reorder, tag, remove, add steps.
//   part  = full, OR the person THIS step is tagged to: tick it, add a note, pin a photo.
// The server enforces the same split; hiding a control here is courtesy, not security.
//
// ⚠️ A TAGGED STEP IS DRAWN LOUDER - orange edge, tinted row, and the name on a chip - because
// she asked for it to "show as a highlight" and because a tag nobody notices is not a hand-off.
const firstName = (n) => String(n || '').split(' ')[0];

// Deadlines. A project has one and a step may have its own (Jessica, 2026-10-08). The chip is
// red once the day has passed and the thing is not done, orange on the day and the day before.
const dueLabel = (ymd) => new Date(ymd + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' });
const dayAfter = (ymd) => { const d = new Date(ymd + 'T12:00:00'); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); };
function DueChip({ dueOn, today, done, prefix = 'Due' }) {
  if (!dueOn) return null;
  const late = !done && today && dueOn < today;
  const soon = !done && today && !late && dueOn <= dayAfter(today);
  const tone = done ? { bg: '#E6ECF1', fg: '#59707E' } : late ? { bg: '#FBE3E0', fg: '#A32014' } : soon ? { bg: '#FFF3ED', fg: '#B8420F' } : { bg: '#E6ECF1', fg: '#003B5C' };
  return (
    <span title={late ? 'Past its deadline' : 'Deadline'}
      style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '2px 9px', background: tone.bg, color: tone.fg, whiteSpace: 'nowrap' }}>
      {late ? 'Overdue \u00b7 ' + dueLabel(dueOn) : prefix + ' ' + dueLabel(dueOn)}
    </span>
  );
}
// A small menu that closes when you click anywhere else.
function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  return { open, setOpen, ref };
}
const popPanel = { position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 30, background: 'var(--surface, #fff)', border: '1px solid var(--line, #D9E2E8)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,59,92,0.16)', padding: 8, minWidth: 210 };

// ⚠️ SEVERAL PEOPLE, OR THE WHOLE TEAM, ON ONE STEP (Jessica, 2026-10-16: "the ability to tag
// whole team or more than one person to a project task"). Tick the people, then Save; nothing
// is sent until Save, so building a list of four does not fire four separate saves and emails.
// "Whole team" means everyone on the board's roster. Each newly tagged person gets the email.
function TagPicker({ step, people, onSave }) {
  const { open, setOpen, ref } = usePopover();
  const tagged = (step.assignees || []).map(a => a.key);
  const [pick, setPick] = useState(tagged);
  const all = people.map(x => x.key);
  const whole = all.length > 1 && all.every(k => pick.includes(k));
  const flip = (k) => setPick(cur => cur.includes(k) ? cur.filter(x => x !== k) : cur.concat(k));
  const label = !tagged.length ? 'Tag teammates...'
    : all.length > 1 && all.every(k => tagged.includes(k)) ? 'Tagged: whole team'
      : 'Tagged: ' + (step.assignees || []).map(a => firstName(a.name)).join(', ');
  const row = { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', fontSize: 13, cursor: 'pointer', borderRadius: 6 };
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button id={'step-tag-' + step.id} onClick={() => { setPick(tagged); setOpen(!open); }}
        title="Tag one or more teammates on this step. It shows on each of their lists."
        style={{
          fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit', borderRadius: 999, padding: '2px 9px', cursor: 'pointer', maxWidth: '100%',
          border: '1px solid ' + (tagged.length ? 'var(--orange)' : 'var(--line, #D9E2E8)'),
          background: tagged.length ? 'var(--orange)' : 'transparent', color: tagged.length ? '#fff' : 'var(--muted, #59707E)',
        }}>{label}</button>
      {open && (
        <div style={popPanel}>
          <label style={{ ...row, fontWeight: 700, color: 'var(--navy)', borderBottom: '1px solid #EDF1F4', borderRadius: 0, marginBottom: 2 }}>
            <input type="checkbox" checked={whole} onChange={() => setPick(whole ? [] : all)} style={{ accentColor: 'var(--orange)' }} />
            Whole team
          </label>
          {people.map(person => (
            <label key={person.key} style={row}>
              <input type="checkbox" checked={pick.includes(person.key)} onChange={() => flip(person.key)} style={{ accentColor: 'var(--orange)' }} />
              {person.name}
            </label>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button onClick={() => { setOpen(false); onSave(step.id, whole ? 'team' : pick); }}
              style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: 'none', background: 'var(--navy)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Save
            </button>
            {tagged.length > 0 && (
              <button onClick={() => { setOpen(false); onSave(step.id, []); }}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line, #D9E2E8)', background: 'transparent', color: 'var(--muted, #59707E)', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </span>
  );
}

// ⚠️ RAISE IT AT A MEETING (Jessica, 2026-10-16: "a feature where a project or task can be
// added to an upcoming meeting's talking points"). It goes into the same agenda notes Greg's
// chats use, and a cron writes those onto that meeting in Outlook. The chip after it says
// which meetings it is already on, until somebody marks it discussed.
function MeetingMenu({ meetings = [], on = [], onPick, compact }) {
  const { open, setOpen, ref } = usePopover();
  if (!meetings.length || !onPick) return null;
  const onSlugs = on.map(m => m.slug);
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <button onClick={() => setOpen(!open)} title="Put this on an upcoming meeting's talking points" aria-label="Put this on a meeting agenda"
        style={compact ? stepIcon : { display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: 'none', color: 'var(--navy)', cursor: 'pointer', padding: 0, fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' }}>
        <CalendarPlus size={compact ? 14 : 13} />{!compact && 'Raise at a meeting'}
      </button>
      {on.map(m => (
        <span key={m.slug} title={'On the talking points for ' + m.label}
          style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '1px 8px', background: '#E6ECF1', color: 'var(--navy)', whiteSpace: 'nowrap' }}>
          On {m.label}
        </span>
      ))}
      {open && (
        <div style={{ ...popPanel, left: 'auto', right: compact ? 0 : 'auto' }}>
          <div style={{ fontSize: 11.5, color: 'var(--muted, #59707E)', padding: '2px 6px 6px' }}>Add to the talking points for:</div>
          {meetings.map(m => (
            <button key={m.slug} disabled={onSlugs.includes(m.slug)} onClick={() => { setOpen(false); onPick(m.slug); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', border: 'none', borderRadius: 6, background: 'none', fontSize: 13, fontFamily: 'inherit', color: onSlugs.includes(m.slug) ? '#93A6B1' : 'var(--ink, #0B1F2A)', cursor: onSlugs.includes(m.slug) ? 'default' : 'pointer' }}>
              {m.label}{onSlugs.includes(m.slug) ? ' (already on it)' : ''}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

const stepIcon = { border: 'none', background: 'none', color: 'var(--muted, #59707E)', cursor: 'pointer', padding: 0, opacity: 0.75, display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, fontFamily: 'inherit', fontSize: 11.5, flexShrink: 0 };

function TaskList({ project, me = { key: '' }, steward = false, canEdit, people = [], today, onToggle, onRemove, onAdd, onReorder, onAddPhoto, onRemovePhoto, onEditTask, onAssign, onAddNote, onRemoveNote, onDue, onAddLink, onRemoveLink, meetings, onAgenda, onPromote }) {
  const [draft, setDraft] = useState('');
  // Finished steps sit at the bottom, folded away (Jessica, 2026-10-16: a completed step "should
  // collapse to the bottom of the list and the next open task moves up to the top"). The server
  // already sends open steps first; this keeps the finished ones out of the way until asked for.
  const [showDone, setShowDone] = useState(false);
  const doneCount = project.tasks.filter(t => t.done).length;
  const shownTasks = showDone ? project.tasks : project.tasks.filter(t => !t.done);
  const [editing, setEditing] = useState(null);   // { id, text }
  const [notesOpen, setNotesOpen] = useState({}); // { [taskId]: true }
  const [noteDraft, setNoteDraft] = useState({}); // { [taskId]: 'text' }
  // A screenshot pinned to one step. One hidden picker serves every step; pinFor remembers
  // which step asked.
  const picker = React.useRef(null);
  const pinFor = React.useRef(null);
  const [pinning, setPinning] = useState(null);
  const [pinMsg, setPinMsg] = useState(null);
  const [linkFor, setLinkFor] = useState(null); // the step whose add-a-link box is open
  const pickPins = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const taskId = pinFor.current;
    if (!files.length || !taskId || !onAddPhoto) return;
    setPinning(taskId); setPinMsg(null);
    const failed = await uploadPhotos(files, (rec) => onAddPhoto(project.id, rec, taskId));
    setPinning(null);
    if (failed) setPinMsg(failText(failed));
  };
  // Drag a step by its handle to reorder. The line shows where it will land.
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null); // { id, after }
  const drop = () => {
    if (dragId == null || !over || over.id === dragId) { setDragId(null); setOver(null); return; }
    const ids = project.tasks.map(t => t.id).filter(id => id !== dragId);
    let at = ids.indexOf(over.id);
    if (at < 0) at = ids.length; else if (over.after) at += 1;
    ids.splice(at, 0, dragId);
    setDragId(null); setOver(null);
    if (onReorder) onReorder(project.id, ids);
  };
  const submit = (e) => {
    e.preventDefault();
    const t = draft.trim();
    if (!t) return;
    setDraft('');
    onAdd(project.id, t);
  };
  const saveEdit = () => {
    if (!editing) return;
    const text = editing.text.trim();
    const was = project.tasks.find(t => t.id === editing.id);
    setEditing(null);
    if (text && was && text !== was.text && onEditTask) onEditTask(editing.id, text);
  };
  const sendNote = (e, taskId) => {
    e.preventDefault();
    const text = String(noteDraft[taskId] || '').trim();
    if (!text || !onAddNote) return;
    setNoteDraft(d => Object.assign({}, d, { [taskId]: '' }));
    onAddNote(taskId, text);
  };

  return (
    <div>
      <div style={{ display: 'grid', gap: 3 }}>
        {shownTasks.map(t => {
          const tags = t.assignees || [];
          const tagged = tags.length > 0;
          const mineStep = tags.some(a => a.key === me.key);
          const part = canEdit || mineStep;
          const notes = t.notes || [];
          const showNotes = !!notesOpen[t.id];
          const isEditing = editing && editing.id === t.id;
          return (
          <div key={t.id} style={tagged ? { background: '#FFF6F0', borderLeft: '3px solid var(--orange)', borderRadius: 6, padding: '2px 8px' } : undefined}>
          <div
            onDragOver={(e) => {
              if (dragId == null) return;
              e.preventDefault();
              const r = e.currentTarget.getBoundingClientRect();
              const after = e.clientY > r.top + r.height / 2;
              if (!over || over.id !== t.id || over.after !== after) setOver({ id: t.id, after });
            }}
            onDrop={(e) => { if (dragId != null) { e.preventDefault(); drop(); } }}
            style={{
            display: 'flex', alignItems: 'flex-start', gap: 9, padding: '4px 0',
            opacity: dragId === t.id ? 0.4 : 1,
            boxShadow: over && over.id === t.id && dragId !== t.id ? (over.after ? '0 2px 0 0 var(--orange)' : '0 -2px 0 0 var(--orange)') : 'none',
          }}>
            {canEdit && project.tasks.length > 1 && (
              <span draggable title="Drag to reorder"
                onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(t.id)); setDragId(t.id); }}
                onDragEnd={() => { setDragId(null); setOver(null); }}
                style={{ cursor: 'grab', color: 'var(--muted, #59707E)', opacity: 0.6, display: 'inline-flex', marginTop: 2, flexShrink: 0 }}>
                <GripVertical size={14} />
              </span>
            )}
            <input
              id={'step-' + t.id} type="checkbox" checked={t.done} disabled={!part}
              onChange={() => onToggle(t.id)} aria-label={'Done: ' + t.text}
              style={{ width: 16, height: 16, accentColor: 'var(--navy)', marginTop: 2, flexShrink: 0, cursor: part ? 'pointer' : 'default' }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              {isEditing ? (
                <input id={'step-edit-' + t.id} autoFocus value={editing.text}
                  onChange={(e) => setEditing({ id: t.id, text: e.target.value })}
                  onBlur={saveEdit}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEdit(); } if (e.key === 'Escape') setEditing(null); }}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '4px 8px', fontSize: 13.5, border: '1px solid var(--navy)', borderRadius: 6, fontFamily: 'inherit' }} />
              ) : (
                <span style={{
                  fontSize: 13.5, lineHeight: 1.45,
                  color: t.done ? '#93A6B1' : 'var(--ink, #0B1F2A)',
                  textDecoration: t.done ? 'line-through' : 'none',
                }}>{t.text}</span>
              )}
              {/* Who it is tagged to. Whoever runs the project picks from the team; everyone
                  else just sees the name. */}
              {(tagged || t.dueOn || (t.agenda && t.agenda.length > 0) || (canEdit && (onAssign || onDue))) && (
                <div style={{ marginTop: 3, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {canEdit && onAssign ? (
                    <TagPicker step={t} people={people} onSave={onAssign} />
                  ) : (
                    <span style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '2px 9px', background: 'var(--orange)', color: '#fff' }}>
                      {mineStep ? (tags.length > 1 ? 'Tagged to you +' + (tags.length - 1) : 'Tagged to you') : 'Tagged: ' + tags.map(a => firstName(a.name)).join(', ')}
                    </span>
                  )}
                  {/* This step's own deadline. Whoever runs the project sets it; everyone sees it. */}
                  <DueChip dueOn={t.dueOn} today={today} done={t.done} />
                  {canEdit && onDue && (
                    <label title="Deadline for this step, separate from the project's" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: 'var(--muted, #59707E)' }}>
                      {t.dueOn ? 'change' : 'Deadline'}
                      <input id={'step-due-' + t.id} type="date" value={t.dueOn || ''} onChange={(e) => onDue(t.id, e.target.value)}
                        style={{ fontSize: 11.5, fontFamily: 'inherit', border: '1px solid var(--line, #D9E2E8)', borderRadius: 6, padding: '1px 4px', color: 'var(--navy)', background: 'var(--surface, #fff)' }} />
                    </label>
                  )}
                  {(t.agenda || []).map(m => (
                    <span key={m.slug} title={'On the talking points for ' + m.label}
                      style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '1px 8px', background: '#E6ECF1', color: 'var(--navy)', whiteSpace: 'nowrap' }}>
                      On {m.label}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {(notes.length > 0 || part) && (
              <button onClick={() => setNotesOpen(o => Object.assign({}, o, { [t.id]: !o[t.id] }))}
                title={notes.length ? 'Notes on this step' : 'Add a note to this step'} aria-label="Notes on this step" aria-expanded={showNotes}
                style={{ ...stepIcon, color: notes.length ? 'var(--navy)' : stepIcon.color, opacity: notes.length ? 1 : 0.75, fontWeight: 700 }}>
                <MessageSquare size={14} />{notes.length > 0 && notes.length}
              </button>
            )}
            {canEdit && onEditTask && !isEditing && (
              <button onClick={() => setEditing({ id: t.id, text: t.text })} title="Edit this step" aria-label="Edit this step" style={stepIcon}>
                <Pencil size={13} />
              </button>
            )}
            {part && onAddPhoto && (
              <button onClick={() => { pinFor.current = t.id; if (picker.current) picker.current.click(); }}
                disabled={pinning === t.id} title="Attach a photo or file to this step" aria-label="Attach a photo or file to this step" style={stepIcon}>
                <Paperclip size={14} />
              </button>
            )}
            {part && onAddLink && (
              <button onClick={() => setLinkFor(linkFor === t.id ? null : t.id)} title="Add a link to this step" aria-label="Add a link to this step" aria-expanded={linkFor === t.id} style={stepIcon}>
                <Link2 size={14} />
              </button>
            )}
            {onAgenda && (
              <MeetingMenu compact meetings={meetings} on={[]} onPick={(series) => onAgenda(project.id, t.id, series)} />
            )}
            {canEdit && onPromote && (
              <button onClick={() => { if (window.confirm('Make "' + t.text + '" its own project?\n\nIt leaves this list and goes on the board as a new project, up for grabs, with its files, links, notes and deadline.')) onPromote(t.id); }}
                title="Make this step its own project" aria-label="Make this step its own project" style={stepIcon}>
                <FolderPlus size={14} />
              </button>
            )}
            {canEdit && (
              <button onClick={() => onRemove(t.id)} title="Remove this step" aria-label="Remove this step" style={{ ...stepIcon, opacity: 0.6 }}>
                <X size={13} />
              </button>
            )}
          </div>
          {((t.photos && t.photos.length > 0) || (t.links && t.links.length > 0) || pinning === t.id || linkFor === t.id) && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '4px 0 8px 26px' }}>
              {(t.photos || []).map(ph => <Thumb key={ph.id} photo={ph} canEdit={part} onRemove={onRemovePhoto} size={60} />)}
              {(t.links || []).map(l => <LinkChip key={l.id} link={l} canEdit={part} onRemove={onRemoveLink} />)}
              {pinning === t.id && <span style={{ fontSize: 12, color: 'var(--muted, #59707E)' }}>Uploading...</span>}
              {linkFor === t.id && (
                <LinkForm idBase={'step-link-' + t.id} onCancel={() => setLinkFor(null)}
                  onAdd={(link) => { setLinkFor(null); onAddLink(project.id, link, t.id); }} />
              )}
            </div>
          )}
          {showNotes && (
            <div style={{ padding: '2px 0 8px 26px', display: 'grid', gap: 6 }}>
              {notes.map(n => (
                <div key={n.id} style={{ fontSize: 12.5, lineHeight: 1.45, background: 'var(--surface, #fff)', border: '1px solid var(--line, #D9E2E8)', borderRadius: 8, padding: '6px 10px' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <b style={{ color: 'var(--navy)' }}>{firstName(n.authorName)}</b>
                    <span style={{ fontSize: 11, color: 'var(--muted, #59707E)' }}>{n.at}</span>
                    <span style={{ flex: 1 }} />
                    {(n.author === me.key || steward) && onRemoveNote && (
                      <button onClick={() => onRemoveNote(n.id)} title="Remove this note" aria-label="Remove this note" style={{ ...stepIcon, marginTop: 0, opacity: 0.6 }}><X size={11} /></button>
                    )}
                  </div>
                  <div style={{ color: 'var(--ink, #0B1F2A)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.text}</div>
                </div>
              ))}
              {notes.length === 0 && !part && <div style={{ fontSize: 12, color: 'var(--muted, #59707E)' }}>No notes yet.</div>}
              {part && onAddNote && (
                <form onSubmit={(e) => sendNote(e, t.id)} style={{ display: 'flex', gap: 6 }}>
                  <input id={'step-note-' + t.id} value={noteDraft[t.id] || ''} onChange={(e) => setNoteDraft(d => Object.assign({}, d, { [t.id]: e.target.value }))}
                    placeholder="Add a note. Everyone on the board sees it."
                    style={{ flex: 1, minWidth: 0, padding: '6px 10px', fontSize: 12.5, border: '1px solid var(--line, #D9E2E8)', borderRadius: 8, fontFamily: 'inherit' }} />
                  <button type="submit" disabled={!String(noteDraft[t.id] || '').trim()}
                    style={{ padding: '0 12px', borderRadius: 8, border: 'none', background: 'var(--navy)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Add</button>
                </form>
              )}
            </div>
          )}
          </div>
          );
        })}
        {onAddPhoto && (
          <input ref={picker} id={'pins-' + project.id} type="file" multiple hidden onChange={pickPins} />
        )}
        {pinMsg && <div style={{ fontSize: 12, color: '#8A3312', padding: '2px 0' }}>{pinMsg}</div>}
        {doneCount > 0 && (
          <button onClick={() => setShowDone(v => !v)} aria-expanded={showDone}
            style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: 'none', color: 'var(--muted, #59707E)', cursor: 'pointer', padding: '4px 0', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>
            {showDone ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <Check size={12} /> {doneCount} done{showDone ? '' : ' (show)'}
          </button>
        )}
        {project.tasks.length === 0 && (
          <div style={{ fontSize: 12.5, color: 'var(--muted, #59707E)', padding: '2px 0 6px' }}>
            No steps yet. Add them as you and Greg talk it through.
          </div>
        )}
      </div>
      {canEdit && (
        <form onSubmit={submit} style={{ marginTop: 8 }}>
          <input
            id={'step-new-' + project.id}
            value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder="+ Add a step..."
            className="cpx-touch"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 11px', fontSize: 13, border: '1px solid var(--line, #D9E2E8)', borderRadius: 8, fontFamily: 'inherit' }}
          />
        </form>
      )}
    </div>
  );
}

// Steps tagged to one person on projects somebody ELSE owns - the part of "what you are
// carrying" that is not a whole project. Only their steps show, and they may tick them, add a
// note and pin a photo; the rest of the project stays with its owner.
function TaggedSteps({ board, forKey, self, cardProps }) {
  const rows = board.projects
    .filter(pr => pr.owner !== forKey && pr.tasks.some(t => (t.assignees || []).some(a => a.key === forKey)))
    .map(pr => Object.assign({}, pr, { tasks: pr.tasks.filter(t => (t.assignees || []).some(a => a.key === forKey)) }));
  if (!rows.length) return null;
  return (
    <Section accent title={self ? 'Steps tagged to you' : 'Steps tagged to them'}
      note={self ? 'one step of somebody else\u2019s project - tick it, add a note or pin a photo and they see it' : 'single steps on somebody else\u2019s project'}>
      <Grid cols={2}>
        {rows.map(pr => (
          <div key={pr.id} style={{ background: 'var(--surface, #fff)', border: '1px solid #F4C3AE', borderRadius: 14, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={DOMAIN_LABEL}>{pr.domain}</div>
              <h3 style={{ margin: '6px 0 0', fontSize: 16, lineHeight: 1.3, fontWeight: 700 }}>{pr.title}</h3>
              <div style={{ marginTop: 3, fontSize: 12.5, color: 'var(--muted, #59707E)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {pr.ownerName ? firstName(pr.ownerName) + '\u2019s project' : 'Nobody has claimed this project yet'}
                <DueChip dueOn={pr.dueOn} today={cardProps.today} prefix="Project due" />
              </div>
            </div>
            <TaskList project={pr} me={cardProps.me} steward={false} canEdit={false} people={cardProps.people} today={cardProps.today}
              onToggle={cardProps.onToggle} onAddPhoto={cardProps.onAddPhoto} onRemovePhoto={cardProps.onRemovePhoto}
              onAddLink={cardProps.onAddLink} onRemoveLink={cardProps.onRemoveLink}
              onAddNote={cardProps.onAddNote} onRemoveNote={cardProps.onRemoveNote}
              meetings={cardProps.meetings} onAgenda={cardProps.onAgenda} />
          </div>
        ))}
      </Grid>
    </Section>
  );
}

// ⚠️ ANY FILE, NOT ONLY PICTURES, AND EVERYONE CAN DOWNLOAD IT. Jessica, 2026-10-09: "upload
// documents to a task. Markdown files, pdf's, word docs and Google Docs... If greg uploads an
// image or file, I should be able to see it and download it if needed." A picture shows as a
// thumbnail; anything else as a named file. Both open in a new tab on click and both carry a
// Download button, which asks storage to hand the file back under its own name. A Google Doc is
// a LINK, not an upload - see LinkChip below.
const isImage = (f) => /^image\//i.test(f.contentType || '') || /\.(png|jpe?g|gif|webp)$/i.test(f.filename || '');
async function downloadFile(file) {
  const r = await r2({ action: 'presign-read', key: file.key, downloadAs: file.filename || 'download' });
  const a = document.createElement('a');
  a.href = r.url; a.rel = 'noopener'; a.download = file.filename || '';
  document.body.appendChild(a); a.click(); a.remove();
}
const tinyBtn = { width: 20, height: 20, borderRadius: 999, border: '1px solid var(--line, #D9E2E8)', background: 'var(--surface, #fff)', color: 'var(--muted, #59707E)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' };

function Thumb({ photo, canEdit, onRemove, size = 84 }) {
  const image = isImage(photo);
  const [url, setUrl] = useState(null);
  const [bad, setBad] = useState(false);
  useEffect(() => {
    if (!image) return undefined; // a document is fetched when it is clicked, not on sight
    let live = true;
    r2({ action: 'presign-read', key: photo.key }).then(r => { if (live) setUrl(r.url); }).catch(() => { if (live) setBad(true); });
    return () => { live = false; };
  }, [photo.key, image]);
  const openFile = async () => {
    try { const r = await r2({ action: 'presign-read', key: photo.key }); window.open(r.url, '_blank', 'noopener'); }
    catch (_) { setBad(true); }
  };
  const name = photo.filename || (image ? 'Photo' : 'File');
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      {image ? (
        <a href={url || undefined} target="_blank" rel="noopener noreferrer" title={name}
          style={{ display: 'block', width: size, height: size, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--line, #D9E2E8)', background: '#EEF2F5' }}>
          {url && !bad
            ? <img src={url} alt={name} onError={() => setBad(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <span style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--muted, #59707E)', textAlign: 'center', padding: 4, boxSizing: 'border-box' }}>{bad ? 'Could not load' : 'Loading...'}</span>}
        </a>
      ) : (
        <button onClick={openFile} title={'Open ' + name}
          style={{ display: 'flex', alignItems: 'center', gap: 7, minHeight: Math.min(size, 40), maxWidth: 240, padding: '6px 30px 6px 10px', borderRadius: 8, border: '1px solid var(--line, #D9E2E8)', background: 'var(--surface, #fff)', color: 'var(--navy)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, textAlign: 'left' }}>
          <FileText size={15} style={{ flexShrink: 0 }} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bad ? 'Could not open' : name}</span>
        </button>
      )}
      <button onClick={() => downloadFile(photo).catch(() => setBad(true))} title={'Download ' + name} aria-label={'Download ' + name}
        style={{ ...tinyBtn, position: 'absolute', bottom: image ? -6 : '50%', right: image ? -6 : 6, transform: image ? 'none' : 'translateY(50%)', color: 'var(--navy)' }}>
        <Download size={11} />
      </button>
      {canEdit && onRemove && (
        <button onClick={() => onRemove(photo.id)} title={'Remove ' + name} aria-label={'Remove ' + name}
          style={{ ...tinyBtn, position: 'absolute', top: -6, right: -6 }}>
          <X size={11} />
        </button>
      )}
    </span>
  );
}

// A link out - a Google Doc, a vendor page, a Dropbox folder. Opens in a new tab.
function LinkChip({ link, canEdit, onRemove }) {
  let host = '';
  try { host = new URL(link.url).hostname.replace(/^www\./, ''); } catch (_) { host = link.url; }
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <a href={link.url} target="_blank" rel="noopener noreferrer" title={link.url}
        style={{ display: 'flex', alignItems: 'center', gap: 7, minHeight: 34, maxWidth: 260, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--line, #D9E2E8)', background: 'var(--surface, #fff)', color: 'var(--navy)', textDecoration: 'none', fontSize: 12.5, fontWeight: 600, boxSizing: 'border-box' }}>
        <ExternalLink size={14} style={{ flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link.label || host}</span>
      </a>
      {canEdit && onRemove && (
        <button onClick={() => onRemove(link.id)} title="Remove this link" aria-label="Remove this link" style={{ ...tinyBtn, position: 'absolute', top: -6, right: -6 }}>
          <X size={11} />
        </button>
      )}
    </span>
  );
}

function LinkForm({ idBase, onAdd, onCancel }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const box = { padding: '6px 10px', fontSize: 12.5, border: '1px solid var(--line, #D9E2E8)', borderRadius: 8, fontFamily: 'inherit', minWidth: 0 };
  return (
    <form onSubmit={(e) => { e.preventDefault(); const u = url.trim(); if (u) onAdd({ url: u, label: label.trim() }); }}
      style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', flex: '1 1 100%' }}>
      <input id={idBase + '-url'} autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste the link (a Google Doc, a web page...)" style={{ ...box, flex: '2 1 220px' }} />
      <input id={idBase + '-label'} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What to call it (optional)" style={{ ...box, flex: '1 1 140px' }} />
      <button type="submit" disabled={!url.trim()} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'var(--navy)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Add link</button>
      <button type="button" onClick={onCancel} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line, #D9E2E8)', background: 'transparent', color: 'var(--muted, #59707E)', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
    </form>
  );
}

// Files and links on the project itself. The file goes straight from the browser to storage,
// then the board records it. A file that will not go up says so and leaves the rest alone.
function Photos({ p, canEdit, onAdd, onRemove, onAddLink, onRemoveLink }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [linking, setLinking] = useState(false);
  const input = React.useRef(null);
  const photos = p.photos || [];
  const links = p.links || [];
  if (!photos.length && !links.length && !canEdit) return null;
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true); setMsg(null);
    const failed = await uploadPhotos(files, (rec) => onAdd(p.id, rec));
    setBusy(false);
    if (failed) setMsg(failText(failed));
  };
  const addBtn = { display: 'flex', alignItems: 'center', gap: 6, minHeight: 36, padding: '0 12px', borderRadius: 8, border: '1px dashed var(--line, #D9E2E8)', background: 'transparent', color: 'var(--navy)', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' };
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {photos.map(ph => <Thumb key={ph.id} photo={ph} canEdit={canEdit} onRemove={onRemove} />)}
        {links.map(l => <LinkChip key={l.id} link={l} canEdit={canEdit} onRemove={onRemoveLink} />)}
        {canEdit && (
          <>
            <input ref={input} id={'photos-' + p.id} type="file" multiple hidden onChange={pick} />
            <button onClick={() => input.current && input.current.click()} disabled={busy}
              title="Add photos, screenshots, PDFs, Word or Markdown files to this project" style={addBtn}>
              <Camera size={14} /> {busy ? 'Uploading...' : 'Add photos or files'}
            </button>
            {onAddLink && !linking && (
              <button onClick={() => setLinking(true)} title="Add a link, such as a Google Doc" style={addBtn}>
                <Link2 size={14} /> Add a link
              </button>
            )}
          </>
        )}
        {linking && (
          <LinkForm idBase={'project-link-' + p.id} onCancel={() => setLinking(false)}
            onAdd={(link) => { setLinking(false); onAddLink(p.id, link); }} />
        )}
      </div>
      {msg && <div style={{ marginTop: 6, fontSize: 12, color: '#8A3312' }}>{msg}</div>}
    </div>
  );
}

function ProjectCard({ p, me, steward, people, today, onClaim, onRelease, onToggle, onRemoveTask, onAddTask, onFinish, onEdit, onReorder, onAddPhoto, onRemovePhoto, onEditTask, onAssign, onAddNote, onRemoveNote, onDue, onAddLink, onRemoveLink, meetings, onAgenda, onPromote, busy }) {
  const mine = p.owner && p.owner === me.key;
  const canEdit = mine || steward;
  // Open by default when it is yours, or when one of its steps is tagged to you.
  const [open, setOpen] = useState(mine || p.tasks.some(t => (t.assignees || []).some(a => a.key === me.key)));
  const claimed = !!p.owner;

  return (
    <div style={{
      background: 'var(--surface, #fff)',
      border: '1px solid ' + (claimed ? 'var(--line, #D9E2E8)' : '#F4C3AE'),
      borderRadius: 14, padding: '18px 20px 16px',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={DOMAIN_LABEL}>{p.domain}</div>
          <h3 style={{ margin: '6px 0 0', fontSize: 17, lineHeight: 1.3, fontWeight: 700 }}>{p.title}</h3>
          {p.dueOn && <div style={{ marginTop: 6 }}><DueChip dueOn={p.dueOn} today={today} done={p.taskCount > 0 && p.doneCount === p.taskCount} /></div>}
        </div>
        {claimed && <Avatar name={p.ownerName || p.owner} />}
      </div>

      {p.why && !claimed && (
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--muted, #59707E)' }}>{p.why}</p>
      )}

      {claimed && p.taskCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ flex: 1, height: 8, borderRadius: 999, background: '#E6ECF1', overflow: 'hidden' }}>
            <div style={{
              width: p.percent + '%', height: '100%', borderRadius: 999,
              background: p.percent === 100 ? 'var(--orange)' : 'var(--navy)',
            }} />
          </div>
          <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted, #59707E)', whiteSpace: 'nowrap' }}>
            {p.doneCount} of {p.taskCount}
          </span>
        </div>
      )}
      {claimed && p.taskCount === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--orange)', fontWeight: 600 }}>
          Claimed, waiting on the sit-down to build the steps
        </div>
      )}

      {claimed && (
        <div>
          <button onClick={() => setOpen(!open)} style={{
            display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: 'none',
            color: 'var(--navy)', cursor: 'pointer', padding: 0, fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit',
          }}>
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Steps{p.taskCount ? ' (' + p.doneCount + '/' + p.taskCount + ')' : ''}
          </button>
          {open && (
            <div style={{ marginTop: 9, borderLeft: '2px solid var(--line, #D9E2E8)', paddingLeft: 13 }}>
              <TaskList project={p} me={me} steward={steward} canEdit={canEdit} people={people} onToggle={onToggle} onRemove={onRemoveTask} onAdd={onAddTask} onReorder={onReorder}
                onAddPhoto={onAddPhoto} onRemovePhoto={onRemovePhoto} onEditTask={onEditTask} onAssign={onAssign} onAddNote={onAddNote} onRemoveNote={onRemoveNote} onDue={onDue} today={today} onAddLink={onAddLink} onRemoveLink={onRemoveLink} meetings={meetings} onAgenda={onAgenda} onPromote={onPromote} />
            </div>
          )}
        </div>
      )}

      <Photos p={p} canEdit={canEdit} onAdd={onAddPhoto} onRemove={onRemovePhoto} onAddLink={onAddLink} onRemoveLink={onRemoveLink} />

      {onAgenda && (meetings || []).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <MeetingMenu meetings={meetings} on={p.agenda || []} onPick={(series) => onAgenda(p.id, null, series)} />
        </div>
      )}

      <div style={{ flex: 1 }} />

      {!claimed ? (
        <div>
          <button className="btn btn-primary" disabled={busy} onClick={() => onClaim(p.id)}
            style={{ width: '100%', minHeight: 44, fontWeight: 700 }}>
            Claim it
          </button>
          <div style={{ marginTop: 8, fontSize: 11.5, textAlign: 'center', color: 'var(--muted, #59707E)' }}>
            No task list until somebody takes it
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          borderTop: '1px solid #EDF1F4', paddingTop: 11, fontSize: 12.5, color: 'var(--muted, #59707E)',
        }}>
          <span style={{ flex: 1, minWidth: 120 }}>
            {p.ownerName || p.owner} has this{p.claimedOn ? ', since ' + p.claimedOn : ''}
          </span>
          {canEdit && p.taskCount > 0 && p.doneCount === p.taskCount && (
            <button onClick={() => onFinish(p.id)} disabled={busy} title="Close this project out"
              style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: 'none', color: 'var(--orange)', cursor: 'pointer', padding: 0, fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' }}>
              <Check size={13} /> Finished
            </button>
          )}
          {canEdit && (
            <button onClick={() => onRelease(p.id)} disabled={busy} title="Put it back on the board"
              style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: 'none', color: 'var(--muted, #59707E)', cursor: 'pointer', padding: 0, fontSize: 12.5, fontFamily: 'inherit' }}>
              <RotateCcw size={12} /> Hand back
            </button>
          )}
          {(steward || mine) && (
            <button onClick={() => onEdit(p)} title="Edit this card"
              style={{ border: 'none', background: 'none', color: 'var(--muted, #59707E)', cursor: 'pointer', padding: 0, fontSize: 12.5, fontFamily: 'inherit' }}>
              Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// The board, and everything a project card can do to it. Shared by this page and by the
// "Your projects" section of My Board, so the two can never offer different tools.
function useTeamBoard(onChanged) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setBoard(await call('board')); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (action, params) => {
    setBusy(true);
    // Keep who is signed in even if a response leaves it out - losing it emptied Mine and hid
    // New project and Edit until a refresh.
    try { const next = await call(action, params); if (next.projects) setBoard(prev => Object.assign({}, next, { me: next.me || (prev && prev.me) })); setErr(null); if (onChanged) onChanged(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const me = (board && board.me) || { key: '', name: '', steward: false };
  const steward = !!me.steward;
  const cardProps = board && {
    me, steward, busy, people: board.roster, today: board.today,
    onClaim: (id) => act('claim', { id }),
    onRelease: (id) => act('release', { id }),
    onToggle: (taskId) => act('task_toggle', { taskId }),
    onRemoveTask: (taskId) => act('task_remove', { taskId }),
    onAddTask: (id, text) => act('task_add', { id, text }),
    onFinish: (id) => act('finish', { id }),
    onEdit: (p) => setEditing(p),
    onReorder: (id, taskIds) => act('task_reorder', { id, taskIds }),
    onAddPhoto: (id, file, taskId) => act('photo_add', Object.assign({ id, taskId: taskId || null }, file)),
    onRemovePhoto: (photoId) => act('photo_remove', { photoId }),
    onAddLink: (id, link, taskId) => act('link_add', Object.assign({ id, taskId: taskId || null }, link)),
    onRemoveLink: (linkId) => act('link_remove', { linkId }),
    onEditTask: (taskId, text) => act('task_edit', { taskId, text }),
    onAssign: (taskId, assignees) => act('task_assign', { taskId, assignees }),
    meetings: board.meetings || [],
    onAgenda: (id, taskId, series) => act('agenda_add', { id, taskId, series }),
    onPromote: (taskId) => act('task_promote', { taskId }),
    onDue: (taskId, dueDate) => act('task_due', { taskId, dueDate }),
    onAddNote: (taskId, text) => act('note_add', { taskId, text }),
    onRemoveNote: (noteId) => act('note_remove', { noteId }),
  };

  const editor = editing && (
    <EditModal
      project={editing}
      steward={steward}
      onClose={() => setEditing(null)}
      onSave={async (vals) => {
        await act(editing.id ? 'update' : 'create', Object.assign({ id: editing.id }, vals));
        setEditing(null);
      }}
      onDelete={editing.id && steward ? async () => {
        if (!window.confirm('Take "' + editing.title + '" off the board? Its steps and history go with it.')) return;
        await act('remove', { id: editing.id });
        setEditing(null);
      } : null}
    />
  );

  return { board, loading, err, me, steward, cardProps, editor, setEditing };
}

// The "Your projects" section of My Board: the same cards as the Mine tab, photos, notes, tags
// and all. Jessica, Oct 30, 2026: once "Hi, Jessica" opened My Board instead of Greg's page, its
// own stripped-down card (tick boxes only) read as every editing tool and photo being gone.
export function MyProjects({ onChanged, navigateTo }) {
  const { board, loading, err, me, cardProps, editor } = useTeamBoard(onChanged);
  if (loading) return <Empty>Loading your projects...</Empty>;
  if (!board) return <Empty>{err || 'Your projects could not be loaded.'}</Empty>;
  const mine = board.projects.filter(p => p.owner === me.key);
  return (
    <>
      {err && (
        <div style={{ background: '#FDECE6', border: '1px solid #F4C3AE', borderRadius: 10, padding: '11px 15px', marginBottom: 18, fontSize: 13.5, color: '#8A3312' }}>
          {err}
        </div>
      )}
      <Section title="Your projects" note="the same projects as the Team board, and every change shows there">
        {mine.length === 0 ? (
          <Empty>
            Nothing claimed yet.{' '}
            <button onClick={() => navigateTo && navigateTo('team-board')}
              style={{ border: 'none', background: 'none', color: 'var(--orange)', cursor: 'pointer', padding: 0, fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', textDecoration: 'underline' }}>
              Go and take one
            </button>.
          </Empty>
        ) : <Grid cols={2}>{mine.map(p => <ProjectCard key={p.id} p={p} {...cardProps} />)}</Grid>}
      </Section>
      <TaggedSteps board={board} forKey={me.key} self cardProps={cardProps} />
      {editor}
    </>
  );
}

export default function TeamBoard({ initialTab = 'team' }) {
  const { board, loading, err, me, cardProps, editor, setEditing } = useTeamBoard();
  const [tab, setTab] = useState(initialTab);
  // Whose projects the board is narrowed to, picked from the Who has what strip.
  const [who, setWho] = useState(null);

  if (loading) return <div className="page-content" style={{ padding: 28 }}>Loading the board...</div>;
  if (!board) {
    return (
      <div className="page-content" style={{ padding: 28 }}>
        <div className="card"><div className="card-body">{err || 'The board could not be loaded.'}</div></div>
      </div>
    );
  }

  const inMotion = board.projects.filter(p => p.owner);
  const pool = board.projects.filter(p => !p.owner);
  const mine = board.projects.filter(p => p.owner === me.key);
  // Open steps tagged to me on somebody else's project count toward "Mine" too.
  const myRow = board.roster.find(r => r.key === me.key);
  const mySteps = (myRow && myRow.steps) || 0;
  const whoRow = who ? board.roster.find(r => r.key === who) : null;
  const theirs = whoRow ? board.projects.filter(p => p.owner === who) : [];

  return (
    <div className="page-content" style={{ padding: '22px 28px 40px' }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
        <Users size={22} color="var(--navy)" />
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: 'var(--navy)' }}>Team</h1>

        <div style={{ display: 'flex', gap: 6 }}>
          {[['team', 'Everything'], ['mine', 'Mine (' + (mine.length + mySteps) + ')'], ['wish', 'Wish list']].map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); setWho(null); }} style={{
              minHeight: 40, padding: '0 16px', borderRadius: 999, border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: tab === key ? 700 : 600, fontFamily: 'inherit',
              background: tab === key ? 'var(--navy)' : 'transparent',
              color: tab === key ? '#fff' : 'var(--muted, #59707E)',
            }}>{label}</button>
          ))}
        </div>

        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>
          {board.inMotion} in motion &middot; <span style={{ color: 'var(--orange)' }}>{board.upForGrabs} waiting for a person</span>
        </div>
        {me.key && (
          <button className="btn btn-outline" onClick={() => setEditing({ id: null, title: '', domain: '', why: '' })}
            style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 40 }}>
            <Plus size={15} /> New project
          </button>
        )}
      </div>

      {/* The Tuesday read, at the TOP by Greg's call on 2026-09-22 - the first question in the
          room is who is carrying what, so it reads before the cards rather than after them.
          Every person, zeros included; an empty chip is the point. */}
      <div style={{ background: 'var(--navy)', borderRadius: 14, padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8FC4E4' }}>Tuesday 9:30</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginTop: 3 }}>Who has what</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', flex: 1 }}>
          {board.roster.map(r => {
            const empty = r.count === 0;
            const picked = who === r.key;
            return (
              <button key={r.key} type="button" aria-pressed={picked}
                title={picked ? 'Show everyone again' : 'Show only what ' + r.name.split(' ')[0] + ' has'}
                onClick={() => { setWho(picked ? null : r.key); if (tab !== 'team') setTab('team'); }}
                style={{
                cursor: 'pointer', fontFamily: 'inherit', outline: picked ? '2px solid var(--orange)' : 'none', outlineOffset: 2,
                display: 'flex', alignItems: 'center', gap: 9, borderRadius: 999, padding: '6px 15px 6px 6px',
                background: empty ? 'transparent' : 'rgba(255,255,255,0.10)',
                border: '1px ' + (empty ? 'dashed rgba(255,255,255,0.28)' : 'solid rgba(255,255,255,0.16)'),
              }}>
                <Avatar name={r.name} size={28} faded={empty} />
                <span style={{ fontSize: 13.5, fontWeight: 600, color: empty ? '#8FC4E4' : '#fff' }}>{r.name.split(' ')[0]}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 12.5, color: empty ? 'var(--orange-light, #EA733D)' : '#8FC4E4' }}>{r.count}</span>
                {r.steps > 0 && <span title={r.steps + ' step' + (r.steps === 1 ? '' : 's') + ' tagged on other projects'} style={{ fontFamily: 'monospace', fontSize: 11.5, color: '#F6B79A' }}>+{r.steps}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {err && (
        <div style={{ background: '#FDECE6', border: '1px solid #F4C3AE', borderRadius: 10, padding: '11px 15px', marginBottom: 18, fontSize: 13.5, color: '#8A3312' }}>
          {err}
        </div>
      )}

      {tab === 'wish' ? <WishList /> : tab === 'mine' ? (
        <>
        <Section title="What you are carrying" note="claimed by you, and every tick here shows on the team view">
          {mine.length === 0
            ? <Empty>Nothing yet. Take one from Up for grabs and Greg will build the steps with you.</Empty>
            : <Grid cols={2}>{mine.map(p => <ProjectCard key={p.id} p={p} {...cardProps} />)}</Grid>}
        </Section>
        <TaggedSteps board={board} forKey={me.key} self cardProps={cardProps} />
        </>
      ) : whoRow ? (
        <>
        <Section title={'What ' + whoRow.name.split(' ')[0] + ' has'} note="click the name again, or Everything, to see the whole board">
          {theirs.length === 0
            ? <Empty>{whoRow.name.split(' ')[0]} is not carrying anything right now.</Empty>
            : <Grid cols={2}>{theirs.map(p => <ProjectCard key={p.id} p={p} {...cardProps} />)}</Grid>}
        </Section>
        <TaggedSteps board={board} forKey={who} self={who === me.key} cardProps={cardProps} />
        </>
      ) : (
        <>
          <Section title="In motion" note="claimed, talked through, and ticking down">
            {inMotion.length === 0
              ? <Empty>Nothing claimed yet.</Empty>
              : <Grid cols={2}>{inMotion.map(p => <ProjectCard key={p.id} p={p} {...cardProps} />)}</Grid>}
          </Section>

          <Section title="Up for grabs" accent
            note="no task list yet, on purpose - claiming one books the conversation where you build it together">
            {pool.length === 0
              ? <Empty>Everything has somebody on it.</Empty>
              : <Grid cols={3}>{pool.map(p => <ProjectCard key={p.id} p={p} {...cardProps} />)}</Grid>}
          </Section>
        </>
      )}

      {editor}
    </div>
  );
}

const Section = ({ title, note, accent, children }) => (
  <div style={{ marginBottom: 30 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 13 }}>
      <h2 style={{ margin: 0, fontSize: 13, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: accent ? 'var(--orange)' : 'var(--navy)' }}>{title}</h2>
      <span style={{ fontSize: 13, color: 'var(--muted, #59707E)' }}>{note}</span>
    </div>
    {children}
  </div>
);

const Grid = ({ cols, children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(' + (cols === 3 ? 300 : 400) + 'px, 100%), 1fr))', gap: 20 }}>
    {children}
  </div>
);

const Empty = ({ children }) => (
  <div style={{ fontSize: 13.5, color: 'var(--muted, #59707E)', padding: '4px 0' }}>{children}</div>
);

function EditModal({ project, steward, onClose, onSave, onDelete }) {
  // A new project from anyone but a steward is theirs straight away. A steward chooses: put it
  // up for grabs (the default), or take it.
  const [takeIt, setTakeIt] = useState(false);
  const [title, setTitle] = useState(project.title || '');
  const [domain, setDomain] = useState(project.domain || '');
  const [why, setWhy] = useState(project.why || '');
  const [dueDate, setDueDate] = useState(project.dueOn || '');
  const input = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, border: '1px solid var(--line, #D9E2E8)', borderRadius: 8, fontFamily: 'inherit' };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(11,31,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 900 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface, #fff)', borderRadius: 14, padding: 24, width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ margin: '0 0 18px', fontSize: 19, fontWeight: 700, color: 'var(--navy)' }}>
          {project.id ? 'Edit the project' : steward ? 'Put a project on the board' : 'Add a project you are working on'}
        </h2>
        <div style={{ display: 'grid', gap: 14 }}>
          <label style={{ display: 'grid', gap: 5, fontSize: 13, fontWeight: 600 }}>
            Title
            <input className="cpx-touch" style={input} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus placeholder="What is the project" />
          </label>
          <label style={{ display: 'grid', gap: 5, fontSize: 13, fontWeight: 600 }}>
            Area
            <input className="cpx-touch" style={input} value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="Website, Accounting, Marketing..." />
          </label>
          <label style={{ display: 'grid', gap: 5, fontSize: 13, fontWeight: 600 }}>
            Why it matters
            <textarea className="cpx-touch" style={{ ...input, minHeight: 92, resize: 'vertical' }} value={why} onChange={(e) => setWhy(e.target.value)}
              placeholder="Enough for somebody to decide whether to take it. The steps come later, with them." />
          </label>
          <label style={{ display: 'grid', gap: 5, fontSize: 13, fontWeight: 600 }}>
            Deadline for the whole project (optional)
            <input id="project-due" type="date" className="cpx-touch" style={{ ...input, maxWidth: 220 }} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--muted, #59707E)' }}>Each step can have its own deadline too, set on the step.</span>
          </label>
        </div>
        {!project.id && (steward ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, cursor: 'pointer' }}>
            <input id="new-project-mine" type="checkbox" checked={takeIt} onChange={(e) => setTakeIt(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--navy)' }} />
            I am taking this one myself (otherwise it goes up for grabs)
          </label>
        ) : (
          <div style={{ marginTop: 14, fontSize: 13, color: 'var(--muted, #59707E)' }}>
            It goes on the board under your name, and you can add its steps right away.
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center' }}>
          <button className="btn btn-primary" style={{ minHeight: 44 }} disabled={!title.trim()} onClick={() => onSave({ title, domain, why, dueDate, mine: takeIt })}>
            {project.id ? 'Save' : steward && !takeIt ? 'Put it up' : 'Add it'}
          </button>
          <button className="btn btn-outline" style={{ minHeight: 44 }} onClick={onClose}>Cancel</button>
          <div style={{ flex: 1 }} />
          {onDelete && <button className="btn btn-danger" style={{ minHeight: 44 }} onClick={onDelete}>Take it off</button>}
        </div>
      </div>
    </div>
  );
}
